import axios from 'axios';
import { IntlShape } from 'react-intl';

// "Extra details" are organisation columns the Ruby backend does not manage
// (is_pto / my_company, devices limit, charging base url, threshold, ...).
// They are read and written through the Express API, AFTER the Ruby add/edit
// succeeded, and only the keys that actually changed are ever sent, so the two
// backends never overwrite each other's columns.

export const DEFAULT_CHARGING_BACKEND_BASE_URL =
  'https://abt.all.api-prod.mwasla.tech/api/v1/transactions';

export type OrganizationKind = 'my_company' | 'is_pto' | '';

export interface OrganizationExtraDetailsState {
  organizationKind: OrganizationKind;
  isSuperOrganisation: boolean;
  maxDevicesCanAdded: string;
  chargingBackendBaseUrl: string;
  thresholdValue: string;
  serviceDayCutoffUtc: string;
  currency: string;
}

// Shape returned by GET /api/v1/organisations/:id/extra-details
// (pg returns numeric / time columns as strings).
export interface OrganizationExtraDetailsApi {
  id: string;
  org_code: string | null;
  code: string | null;
  current_devices_numbers: string | number | null;
  is_pto: boolean | null;
  my_company: boolean | null;
  is_super_organisation: boolean | null;
  max_devices_can_added: string | number | null;
  charging_backend_baseurl: string | null;
  threshold_value: string | number | null;
  service_day_cutoff_utc: string | null;
  currency: string | null;
}

// Body of PUT /api/v1/organisations/:id/extra-details (every key optional).
export interface OrganizationExtraDetailsPayload {
  is_pto?: boolean;
  my_company?: boolean;
  is_super_organisation?: boolean;
  max_devices_can_added?: number;
  charging_backend_baseurl?: string;
  threshold_value?: number;
  service_day_cutoff_utc?: string;
  currency?: string;
}

export const defaultExtraDetailsState: OrganizationExtraDetailsState = {
  organizationKind: '',
  isSuperOrganisation: false,
  maxDevicesCanAdded: '1000',
  chargingBackendBaseUrl: DEFAULT_CHARGING_BACKEND_BASE_URL,
  thresholdValue: '0',
  serviceDayCutoffUtc: '03:00:00',
  currency: 'EGP',
};

const toNumberString = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === ''
    ? ''
    : String(Number(value));

export const extraDetailsApiToState = (
  data: OrganizationExtraDetailsApi
): OrganizationExtraDetailsState => {
  let organizationKind: OrganizationKind = '';

  if (data.my_company && !data.is_pto) organizationKind = 'my_company';
  else if (data.is_pto && !data.my_company) organizationKind = 'is_pto';

  return {
    organizationKind,
    isSuperOrganisation: Boolean(data.is_super_organisation),
    maxDevicesCanAdded: toNumberString(data.max_devices_can_added),
    chargingBackendBaseUrl: data.charging_backend_baseurl ?? '',
    thresholdValue: toNumberString(data.threshold_value),
    serviceDayCutoffUtc: data.service_day_cutoff_utc ?? '03:00:00',
    currency: data.currency ?? 'EGP',
  };
};

const normalizeTime = (value: string): string =>
  value.length === 5 ? `${value}:00` : value;

// Form state -> API keys. `organizationKind` '' (legacy org that has neither
// flag) produces no is_pto / my_company keys.
const stateToFullPayload = (
  state: OrganizationExtraDetailsState
): OrganizationExtraDetailsPayload => ({
  ...(state.organizationKind
    ? {
        my_company: state.organizationKind === 'my_company',
        is_pto: state.organizationKind === 'is_pto',
      }
    : {}),
  is_super_organisation: state.isSuperOrganisation,
  max_devices_can_added: Number(state.maxDevicesCanAdded),
  charging_backend_baseurl: state.chargingBackendBaseUrl.trim(),
  threshold_value: Number(state.thresholdValue),
  service_day_cutoff_utc: normalizeTime(state.serviceDayCutoffUtc.trim()),
  currency: state.currency.trim().toUpperCase(),
});

// Everything (used by the Add modal, which saves the whole group once).
export const buildExtraDetailsPayload = (
  state: OrganizationExtraDetailsState
): OrganizationExtraDetailsPayload => stateToFullPayload(state);

// Only the keys whose value differs from `initial` (used by Edit, so inputs the
// user did not touch are never written back).
export const buildChangedExtraDetailsPayload = (
  current: OrganizationExtraDetailsState,
  initial: OrganizationExtraDetailsState
): OrganizationExtraDetailsPayload => {
  const next = stateToFullPayload(current) as Record<string, unknown>;
  const prev = stateToFullPayload(initial) as Record<string, unknown>;
  const changed: Record<string, unknown> = {};

  Object.keys(next).forEach(key => {
    if (next[key] !== prev[key]) changed[key] = next[key];
  });

  // my_company / is_pto are validated together by the API: send both or none.
  if ('my_company' in changed || 'is_pto' in changed) {
    changed.my_company = next.my_company;
    changed.is_pto = next.is_pto;
  }

  return changed as OrganizationExtraDetailsPayload;
};

interface Errors {
  [key: string]: string;
}

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

// `allowEmptyKind`: edit of a legacy org that has neither flag set may keep it
// unchanged; a new / changed org must pick one.
export const validateExtraDetails = (
  values: OrganizationExtraDetailsState,
  intl: IntlShape,
  allowEmptyKind = false
): Errors => {
  const errors: Errors = {};
  const required = intl.formatMessage({ id: 'requiredValidation' });

  if (!values.organizationKind && !allowEmptyKind) {
    errors.organizationKind = required;
  }

  const devices = Number(values.maxDevicesCanAdded);

  if (values.maxDevicesCanAdded === '') {
    errors.maxDevicesCanAdded = required;
  } else if (!Number.isInteger(devices) || devices < 0 || devices > 99999999) {
    errors.maxDevicesCanAdded = intl.formatMessage({
      id: 'orgInvalidMaxDevices',
    });
  }

  const url = values.chargingBackendBaseUrl.trim();

  if (!url) {
    errors.chargingBackendBaseUrl = required;
  } else if (!/^https?:\/\/\S+$/i.test(url) || url.length > 255) {
    errors.chargingBackendBaseUrl = intl.formatMessage({
      id: 'orgInvalidChargingUrl',
    });
  }

  const threshold = Number(values.thresholdValue);

  if (values.thresholdValue === '') {
    errors.thresholdValue = required;
  } else if (!Number.isFinite(threshold) || Math.abs(threshold) > 99999.99999) {
    errors.thresholdValue = intl.formatMessage({
      id: 'orgInvalidThreshold',
    });
  }

  if (!values.serviceDayCutoffUtc) {
    errors.serviceDayCutoffUtc = required;
  } else if (!TIME_REGEX.test(values.serviceDayCutoffUtc.trim())) {
    errors.serviceDayCutoffUtc = intl.formatMessage({
      id: 'orgInvalidCutoff',
    });
  }

  if (!values.currency.trim()) {
    errors.currency = required;
  } else if (!/^[A-Za-z]{3}$/.test(values.currency.trim())) {
    errors.currency = intl.formatMessage({ id: 'orgInvalidCurrency' });
  }

  return errors;
};

// ─── API (Express backend) ───────────────────────────────────────────────────

const getAuthHeaders = () => {
  try {
    const authData = localStorage.getItem('authData');
    const token = authData ? JSON.parse(authData)?.userToken : null;

    return {
      'access-token': token?.access_token,
      uid: token?.uid,
      client: token?.client,
    };
  } catch {
    return {};
  }
};

const extraDetailsUrl = (organizationId: string) =>
  `${process.env.REACT_APP_ABT_ALL_APIS_BASE_URL}/api/v1/organisations/${organizationId}/extra-details`;

export const fetchOrganizationExtraDetails = async (
  organizationId: string
): Promise<OrganizationExtraDetailsApi> => {
  const res = await axios.get(extraDetailsUrl(organizationId), {
    headers: getAuthHeaders(),
  });

  return res.data.data;
};

export const saveOrganizationExtraDetails = async (
  organizationId: string,
  payload: OrganizationExtraDetailsPayload
): Promise<OrganizationExtraDetailsApi> => {
  const res = await axios.put(extraDetailsUrl(organizationId), payload, {
    headers: getAuthHeaders(),
  });

  return res.data.data;
};

export const getExtraDetailsErrorMessage = (error: any): string =>
  error?.response?.data?.message || error?.message || String(error);
